import { useEffect, useRef, useState } from "react";
import Header from "@/components/Header";
import { api, getAxiosErrorMessage, API_BASE } from "@/lib/api";
import {
  Plus, Loader2, Users as UsersIcon, Mail, Phone, MapPin, Pencil, Trash2, Image as ImageIcon, X,
  Upload,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SupplierImportDialog from "@/components/SupplierImportDialog";
import SegmentedFilter from "@/components/SegmentedFilter";
import { KIND_LABELS, KIND_ORDER, repartitionParNature } from "@/lib/supplierKinds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function LogoAvatar({ fileId, name, size = 48 }) {
  if (fileId) {
    return (
      <img
        src={`${API_BASE}/files/${fileId}/download`}
        alt={name}
        className="rounded-xl object-contain bg-white border border-slate-200 shadow-sm"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <UsersIcon size={size * 0.4} strokeWidth={1.75} />
    </div>
  );
}

function makeCrudPage({ title, subtitle, resource, extraFields = [], testPrefix, supplierBulkImport = false, kindFilter = false }) {
  return function CrudPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const { user } = useAuth();
    // Option volontairement nommee d'apres les fournisseurs : le dialogue
    // d'import ecrit dans /suppliers/import, l'activer sur Clients enverrait
    // des clients dans le fichier fournisseurs.
    // L'import de masse est reserve aux administrateurs. Le backend applique
    // la meme regle : masquer le bouton ne protege rien a lui seul.
    const canImport = supplierBulkImport && user?.role === "admin";

    const [kind, setKind] = useState("all");
    // Les compteurs viennent de la liste COMPLETE, chargée une fois : filtrer
    // côté serveur puis compter sur le résultat ne donnerait qu'un seul chiffre.
    const [repartition, setRepartition] = useState({});

    const fetchList = async (natureDemandee = kind) => {
      setLoading(true);
      try {
        // Le filtrage passe par `?kind=`, déjà offert par le backend : aucune
        // logique serveur n'est ajoutée.
        const params = kindFilter && natureDemandee !== "all" ? { kind: natureDemandee } : {};
        const requetes = [api.get(`/${resource}`, { params })];
        if (kindFilter) requetes.push(api.get(`/${resource}`));
        const [liste, complete] = await Promise.all(requetes);
        setItems(liste.data);
        if (kindFilter) setRepartition(repartitionParNature(complete.data));
      } finally {
        setLoading(false);
      }
    };
    // `fetchList` est recréée à chaque rendu : l'ajouter aux dépendances
    // déclencherait une boucle de requêtes.
    useEffect(() => {
      fetchList(kind);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind]);

    const handleDelete = async (id) => {
      if (!window.confirm("Confirmer la suppression ?")) return;
      try {
        await api.delete(`/${resource}/${id}`);
        toast.success("Supprimé");
        fetchList();
      } catch (e) { toast.error(getAxiosErrorMessage(e)); }
    };

    return (
      <>
        <Header
          title={title}
          subtitle={subtitle}
          actions={
            <div className="flex items-center gap-2">
            {canImport ? (
              <Button
                className="bg-white/10 hover:bg-white/20 text-white border-0"
                onClick={() => setImportOpen(true)}
                data-testid={`${testPrefix}-import-btn`}
              >
                <Upload size={16} className="mr-1.5" /> Importer
              </Button>
            ) : null}
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-white/10 hover:bg-white/20 text-white border-0"
                  onClick={() => setEditing(null)}
                  data-testid={`${testPrefix}-new-btn`}
                >
                  <Plus size={16} className="mr-1.5" /> Ajouter
                </Button>
              </DialogTrigger>
              <CrudDialog
                extraFields={extraFields}
                editing={editing}
                resource={resource}
                testPrefix={testPrefix}
                onClose={() => { setOpen(false); setEditing(null); }}
                onSaved={() => { setOpen(false); setEditing(null); fetchList(); }}
              />
            </Dialog>
            </div>
          }
        />
        {canImport ? (
          <SupplierImportDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            onImported={fetchList}
          />
        ) : null}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 space-y-5">
          {kindFilter ? (
            <SegmentedFilter
              value={kind}
              onChange={setKind}
              options={[
                { value: "all", label: "Tous" },
                ...KIND_ORDER.map((k) => ({ value: k, label: KIND_LABELS[k] })),
              ]}
              counts={repartition}
              ariaLabel="Nature du tiers"
              testId="kind"
            />
          ) : null}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm text-center py-16 text-slate-500">
              <UsersIcon size={32} className="mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
              Aucun enregistrement.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((it) => (
                <div
                  key={it.id}
                  data-testid={`${testPrefix}-card-${it.id}`}
                  className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-all p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <LogoAvatar fileId={it.logo_file_id} name={it.name} />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-900 font-heading truncate">{it.name}</h3>
                        {it.contact_name ? (<div className="text-sm text-slate-600 mt-0.5 truncate">{it.contact_name}</div>) : null}
                        {it.category ? (<div className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-semibold">{it.category}</div>) : null}
                        {it.responsible_name ? (<div className="text-xs text-blue-700 mt-1 font-medium truncate">Resp. {it.responsible_name}</div>) : null}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => { setEditing(it); setOpen(true); }}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                        data-testid={`${testPrefix}-edit-${it.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(it.id)}
                        className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                        data-testid={`${testPrefix}-delete-${it.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                    {it.email ? (<div className="flex items-center gap-2"><Mail size={13} className="text-slate-400" /> <span className="truncate">{it.email}</span></div>) : null}
                    {it.phone ? (<div className="flex items-center gap-2"><Phone size={13} className="text-slate-400" /> {it.phone}</div>) : null}
                    {it.city || it.postal_code ? (<div className="flex items-center gap-2"><MapPin size={13} className="text-slate-400" /> {[it.postal_code, it.city].filter(Boolean).join(" ")}</div>) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </>
    );
  };
}

function CrudDialog({ editing, resource, extraFields, testPrefix, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    name: "", contact_name: "", email: "", phone: "", address: "", city: "", notes: "",
    ...(extraFields.reduce((a, f) => ({ ...a, [f.key]: "" }), {})),
  });
  const [saving, setSaving] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [currentLogoId, setCurrentLogoId] = useState(editing?.logo_file_id || null);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Annuaire des responsables possibles, charge seulement si un champ le
  // demande. La route renvoie `id` + `name` des comptes actifs uniquement.
  const besoinAnnuaire = extraFields.some(
    (f) => f.type === "user" || f.type === "users",
  );
  useEffect(() => {
    if (!besoinAnnuaire) return;
    api.get("/users/assignable")
      .then(({ data }) => setAssignableUsers(data))
      .catch(() => setAssignableUsers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [besoinAnnuaire]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let savedEntity;
      if (editing) {
        const { data } = await api.patch(`/${resource}/${editing.id}`, form);
        savedEntity = data;
      } else {
        const { data } = await api.post(`/${resource}`, form);
        savedEntity = data;
      }
      toast.success(editing ? "Mis à jour" : "Ajouté");
      onSaved(savedEntity);
    } catch (err) { toast.error(getAxiosErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!editing?.id) {
      toast.error("Enregistrez d'abord la fiche pour ajouter un logo");
      return;
    }
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Format image requis (PNG/JPG/WebP)");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/${resource}/${editing.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCurrentLogoId(data.logo_file_id);
      toast.success("Logo mis à jour");
    } catch (e) { toast.error(getAxiosErrorMessage(e)); }
    finally { setUploadingLogo(false); }
  };

  const removeLogo = async () => {
    if (!editing?.id) return;
    try {
      await api.delete(`/${resource}/${editing.id}/logo`);
      setCurrentLogoId(null);
      toast.success("Logo retiré");
    } catch (e) { toast.error(getAxiosErrorMessage(e)); }
  };

  return (
    <DialogContent className="max-w-xl rounded-2xl">
      <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier" : "Ajouter"}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Logo section (visible only when editing an existing entity) */}
        {editing?.id ? (
          <div className="md:col-span-2 p-3 rounded-xl bg-slate-50 border border-slate-100">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Logo</Label>
            <div className="mt-2 flex items-center gap-3">
              <LogoAvatar fileId={currentLogoId} name={form.name} size={56} />
              <div className="flex-1 min-w-0 flex flex-wrap gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                  data-testid={`${testPrefix}-logo-input`}
                />
                <Button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingLogo}
                  variant="outline"
                  className="rounded-xl h-9"
                  data-testid={`${testPrefix}-logo-upload-btn`}
                >
                  {uploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ImageIcon size={14} className="mr-1.5" />}
                  {currentLogoId ? "Remplacer" : "Uploader"}
                </Button>
                {currentLogoId ? (
                  <Button
                    type="button"
                    onClick={removeLogo}
                    variant="outline"
                    className="rounded-xl h-9 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    data-testid={`${testPrefix}-logo-remove-btn`}
                  >
                    <X size={14} className="mr-1" /> Retirer
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <div className="md:col-span-2">
          <Label>Nom</Label>
          <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} required className="rounded-xl mt-1.5" data-testid={`${testPrefix}-name-input`} />
        </div>
        <div>
          <Label>Contact</Label>
          <Input value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} className="rounded-xl mt-1.5" />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="rounded-xl mt-1.5" />
        </div>
        <div>
          <Label>Téléphone</Label>
          <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="rounded-xl mt-1.5" />
        </div>
        {extraFields.map((f) => (
          <div key={f.key} className={f.full ? "md:col-span-2" : undefined}>
            <Label>{f.label}</Label>
            {f.type === "select" ? (
              <select
                value={form[f.key] || f.options?.[0]?.value || ""}
                onChange={(e) => set(f.key, e.target.value)}
                className="w-full h-10 rounded-xl mt-1.5 border border-slate-200 bg-white px-3 text-sm"
                data-testid={`${testPrefix}-${f.key.replace(/_/g, "-")}-select`}
              >
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : f.type === "user" ? (
              <select
                // Identifiant en valeur, jamais le nom : le nom peut changer.
                value={form[f.key] || ""}
                onChange={(e) => set(f.key, e.target.value || null)}
                className="w-full h-10 rounded-xl mt-1.5 border border-slate-200 bg-white px-3 text-sm"
                data-testid={`${testPrefix}-${f.key.replace(/_/g, "-")}-select`}
              >
                <option value="">Aucun responsable</option>
                {assignableUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            ) : f.type === "users" ? (
              // Plusieurs destinataires, distincts du responsable : qui PILOTE
              // la relation et qui est INFORMÉ de ce qui s'y passe sont deux
              // notions. Les confondre obligerait à nommer responsable toute
              // personne devant simplement être tenue au courant.
              <div
                className="mt-1.5 max-h-40 space-y-1 overflow-y-auto rounded-xl border border-slate-200 p-2"
                data-testid={`${testPrefix}-${f.key.replace(/_/g, "-")}-list`}
              >
                {assignableUsers.length === 0 ? (
                  <p className="px-1 py-2 text-xs text-slate-400">Aucun compte disponible.</p>
                ) : (
                  assignableUsers.map((u) => {
                    const selectionnes = form[f.key] || [];
                    const coche = selectionnes.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 text-sm hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={coche}
                          onChange={() =>
                            set(
                              f.key,
                              // Uniquement des identifiants, jamais des noms.
                              coche
                                ? selectionnes.filter((id) => id !== u.id)
                                : [...selectionnes, u.id],
                            )
                          }
                          className="h-3.5 w-3.5 rounded border-slate-300"
                          data-testid={`${testPrefix}-${f.key.replace(/_/g, "-")}-${u.id}`}
                        />
                        <span className="text-slate-700">{u.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
            ) : (
              <Input
                value={form[f.key] || ""}
                onChange={(e) => set(f.key, e.target.value)}
                className="rounded-xl mt-1.5"
                data-testid={`${testPrefix}-${f.key.replace(/_/g, "-")}-input`}
              />
            )}
          </div>
        ))}
        <div>
          <Label>Ville</Label>
          <Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} className="rounded-xl mt-1.5" />
        </div>
        <div className="md:col-span-2">
          <Label>Adresse</Label>
          <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="rounded-xl mt-1.5" />
        </div>
        <div className="md:col-span-2">
          <Label>Notes</Label>
          <Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-xl mt-1.5" />
        </div>
        {!editing ? (
          <div className="md:col-span-2 text-xs text-slate-500">
            Vous pourrez ajouter un logo après la création de la fiche.
          </div>
        ) : null}
        <DialogFooter className="md:col-span-2">
          <Button type="button" variant="ghost" onClick={onClose}>Fermer</Button>
          <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid={`${testPrefix}-save-btn`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Enregistrer
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

export const Clients = makeCrudPage({
  title: "Clients",
  subtitle: "Fichier clients pour les litiges · Logos configurables",
  resource: "clients",
  testPrefix: "client",
});

export const Suppliers = makeCrudPage({
  title: "Fournisseurs",
  subtitle: "Fournisseurs et transporteurs · Import en masse, responsables et logos",
  resource: "suppliers",
  extraFields: [
    {
      key: "kind",
      label: "Type de tiers",
      type: "select",
      options: [
        { value: "fournisseur", label: "Fournisseur" },
        { value: "transporteur", label: "Transporteur" },
        { value: "les_deux", label: "Fournisseur et transporteur" },
      ],
    },
    { key: "responsible_user_id", label: "Responsable", type: "user" },
    {
      key: "notification_user_ids",
      label: "Destinataires des notifications",
      type: "users",
    },
    { key: "siret", label: "SIRET" },
    { key: "contact_role", label: "Fonction du contact" },
    { key: "mobile", label: "Mobile" },
    { key: "website", label: "Site internet" },
    { key: "category", label: "Catégorie" },
    { key: "postal_code", label: "Code postal" },
  ],
  testPrefix: "supplier",
  supplierBulkImport: true,
  kindFilter: true,
});
